<template>
  <div class="greeting-card-extra-v3" v-if="productGrettingCardVariants.length && !greetingCardDisable">
    <div class="form-group">
      <div class="label-v3" :class="{ 'label-v3--centered': customItems && customItems.length > 0 }">
        <template v-if="dynamicPrice && title">
          {{ parsedInfo.title }}
          <span v-show="dynamicTotalPrice"
            >(+ {{ formatPrice(dynamicTotalPrice) }} USD)</span
          >
        </template>
        <template v-else-if="title">{{ parsedInfo.title }}</template>
        <template v-else>
          Buy greeting cards (+
          {{ formatPrice(firstVariant?.price) }} USD)
          <span
            class="greeting-card__tag-sales"
            v-if="firstVariant?.compareAtPrice && percentDiscount"
            >Sale {{ percentDiscount }}%
          </span>
          <del
            class="greeting-card__compare-price"
            v-if="firstVariant?.compareAtPrice"
            >{{ formatPrice(firstVariant?.compareAtPrice) }} USD</del
          >
        </template>
      </div>
      
      <div class="cards-selection-v3" :class="{ 'cards-selection-v3--history': customItems && customItems.length > 0 }">
        <div
          v-for="(item, index) in productGrettingCardVariants"
          :key="index"
          class="card-option-v3"
          :class="{ 
            'card-option-v3--active': indexChoosedArr.includes(index),
            'card-option-v3--expanded': indexChoosed == index 
          }"
          @click="selecteMultipleProductVariant(index)"
        >
          <div class="card-option-v3__img">
            <template v-if="customItems && customItems.length > 0">
              <img :src="item?.image" />
            </template>
            <sib-image v-else v-bind="getImageProductVariant(item)" />
          </div>
          <transition name="check-fade">
            <div class="card-option-v3__check" v-if="indexChoosedArr.includes(index)">
              <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
                <path d="M1 4L4.5 7.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </transition>
        </div>

        <!-- No Option Card -->
        <div
          v-if="showNoOption"
          class="card-option-v3 card-option-v3--no"
          :class="{ 'card-option-v3--active': indexChoosedArr.includes(productGrettingCardVariants.length) }"
          @click="selecteMultipleProductVariant(productGrettingCardVariants.length)"
        >
          <div class="card-option-v3__img">
            <span>No</span>
          </div>
          <transition name="check-fade">
            <div class="card-option-v3__check" v-if="indexChoosedArr.includes(productGrettingCardVariants.length)">
              <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
                <path d="M1 4L4.5 7.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="dynamicTotalPrice" class="price-value-v3" :style="{ visibility: dynamicTotalPrice > 0 ? 'visible' : 'hidden' }">
        +{{ formatPrice(dynamicTotalPrice) }}
      </div>
    </div>

    <template v-if="showUpsellTable">
      <div class="upsell-divider-v3"></div>

      <div class="upsell-table-v3">
        <h3 class="upsell-table-v3__title">Buy more save more!</h3>
        <div class="upsell-table-v3__row">
          <span class="label">Buy 2 cards</span>
          <span class="value">$5.99</span>
        </div>
        <div class="upsell-table-v3__row">
          <span class="label">Buy 3 cards</span>
          <span class="value">$7.99</span>
        </div>
        <div class="upsell-table-v3__row">
          <span class="label">Buy 5 cards</span>
          <span class="value">$9.99</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import { NullOrType, Product } from '@/types'
import { computed, onMounted, ref, watch } from 'vue'
import { QUERY_STRING_NO_CACHE } from '@/utils'
import { formatPrice } from '@/utils/product'
import { useRoute } from 'vue-router'
import useRequestLocationStore from '@/store/requestLocation'

interface Props {
  handle?: string
  title: string
  multipleSelection?: boolean
  dynamicPrice?: boolean
  showUpsellTable?: boolean
  showNoOption?: boolean
  customItems?: any[]
  selectedIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  handle: '',
  multipleSelection: true,
  dynamicPrice: false,
  showUpsellTable: false,
  showNoOption: false,
  customItems: () => [],
  selectedIndex: -1
})

const emit = defineEmits<{
  (e: 'greeting-card-selected', value: any): void
  (e: 'item-selected', value: any): void
}>()

const route = useRoute()
const productStore = useProductStore()
const requestLocation = useRequestLocationStore()

const product = ref<NullOrType<Product>>(null)
const indexChoosed = ref(-1)
const variantSelected = ref<any>(null)
const indexChoosedArr = ref<number[]>([])
const variantSelectedArr = ref<any[]>([])
const firstVariant = ref<any>(null)
const greetingCardDisable = ref(false)

const productGrettingCardVariants = computed(() => {
  if (props.customItems && props.customItems.length > 0) {
    return props.customItems
  }
  return product.value?.variants || []
})

const parsedInfo = computed(() => {
  const result = {
    title: props.title || 'Buy greeting cards',
    price: '' // default fallback
  }

  if (props.title) {
    const priceRegex = /\(\+\s?\$([\d\.]+)\s?USD\)/i
    const match = props.title.match(priceRegex)
    
    if (match) {
      result.price = `+$${match[1]}`
      result.title = props.title.replace(match[0], '').trim()
    }
  }

  return result
})

const percentDiscount = computed(() => {
  if (!firstVariant.value || !firstVariant.value.compareAtPrice) return 0
  return Math.round(
    ((firstVariant.value.compareAtPrice - firstVariant.value.price) /
      firstVariant.value.compareAtPrice) *
      100
  )
})

const dynamicTotalPrice = computed(() => {
  if (props.multipleSelection) {
    return variantSelectedArr.value.length
      ? variantSelectedArr.value.reduce(
          (sum, item) => sum + (item?.price || 0),
          0
        )
      : 0
  }
  return variantSelected.value?.price || 0
})

onMounted(() => {
  if (!props.customItems || props.customItems.length === 0) {
    getProductByHandle()
  }
})

watch(
  () => variantSelected.value,
  () => {
    emit('greeting-card-selected', {
      greetingCardVariantSelected: variantSelected.value,
      quantity: 1,
      productHandle: props.handle || '',
    })
  }
)

watch(() => variantSelectedArr.value, (val) => {
  if (props.customItems && props.customItems.length > 0) return
  emit('greeting-card-selected', {
    greetingCardVariantArrSelected: val || [],
    productHandle: props.handle || '',
    multiple: props.multipleSelection,
  })
}, { deep: true })

watch(() => props.selectedIndex, (val) => {
  if (props.customItems && props.customItems.length > 0) {
    indexChoosedArr.value = val !== undefined && val !== -1 ? [val] : []
  }
}, { immediate: true })

async function getProductByHandle() {
  if (!props.handle) return
  const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
  try {
    product.value = await productStore.getProductByHandle({
      handle: props.handle,
      isForceLoadNewData,
    })
    if (product.value?.variants?.[0]) {
      firstVariant.value = product.value?.variants[0]
    }

    greetingCardDisable.value = !!(product.value?.handle.includes('ipatugrc00424') &&
      requestLocation.countryCode && requestLocation.countryCode.toUpperCase() != 'US')
    
    // Default to nothing chosen or NO
    indexChoosedArr.value = props.showNoOption ? [product.value?.variants?.length || 0] : []
    variantSelectedArr.value = []

    if (props.handle.includes('ipatugrc00424')) {
      selecteProductVariant(0)
    }
  } catch (error) {}
}

function selecteProductVariant(index: number) {
  indexChoosed.value = index
  if (!productGrettingCardVariants.value?.length) return
  if (
    indexChoosed.value == productGrettingCardVariants.value?.length ||
    indexChoosed.value == -1
  ) {
    variantSelected.value = null
    indexChoosedArr.value = [productGrettingCardVariants.value?.length]
  } else {
    variantSelected.value = productGrettingCardVariants.value[index]
    indexChoosedArr.value = [index]
  }
}

function selecteMultipleProductVariant(index: number) {
  // Logic for V2 history images
  if (props.customItems && props.customItems.length > 0) {
    indexChoosedArr.value = [index]
    emit('item-selected', { index, item: props.customItems[index] })
    return
  }

  if (!props.multipleSelection) {
    selecteProductVariant(index)
    return
  }

  if (!productGrettingCardVariants.value?.length) return

  const indexNoChoose = indexChoosedArr.value.findIndex(
    (item) => item == productGrettingCardVariants.value?.length
  )

  if (index == productGrettingCardVariants.value?.length) {
    // click option no choose any option
    indexChoosed.value = index
    variantSelectedArr.value = []

    if (indexNoChoose == -1) {
      indexChoosedArr.value.push(productGrettingCardVariants.value?.length)
    }

    indexChoosedArr.value = indexChoosedArr.value.filter(
      (item) => item == productGrettingCardVariants.value?.length
    )
    variantSelectedArr.value = []
  } else {
    // choose option
    const indexOfIndexChoosedArr = indexChoosedArr.value.findIndex(
      (item) => item == index
    )
    const indexOfVariantSelectedArr = variantSelectedArr.value.findIndex(
      (item) => item.id == productGrettingCardVariants.value[index].id
    )
    if (indexChoosed.value != index) {
      indexChoosed.value = index

      if (indexNoChoose > -1) indexChoosedArr.value.splice(indexNoChoose, 1)

      if (indexOfIndexChoosedArr == -1) {
        indexChoosedArr.value.push(index)
      }

      if (indexOfVariantSelectedArr == -1) {
        variantSelectedArr.value.push(productGrettingCardVariants.value[index])
      }
    } else {
      indexChoosed.value = index

      if (indexOfIndexChoosedArr > -1) {
        indexChoosedArr.value.splice(indexOfIndexChoosedArr, 1)
      } else {
        indexChoosedArr.value.push(index)
      }

      if (indexOfVariantSelectedArr > -1) {
        variantSelectedArr.value.splice(indexOfVariantSelectedArr, 1)
      } else {
        variantSelectedArr.value.push(productGrettingCardVariants.value[index])
      }
    }
  }
}

function getImageProductVariant(productVariant: any) {
  return product.value?.images?.find(
    (item) => item.id == productVariant?.image?.id
  ) || { src: '', alt: '', width: 1000, height: 1000 }
}
</script>

<style lang="scss" scoped>
.greeting-card-extra-v3 {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.toggle-group-v3 {
  display: flex !important;
  gap: 58px !important;
  margin-top: 12px;
}

.toggle-btn-v3 {
  width: 90px !important;
  height: 90px !important;
  border-width: 1px 2px 2px 1px !important;
  border-style: solid !important;
  border-color: #000 !important;
  background: #fff !important;
  font-size: 26px !important;
  font-weight: 700 !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease !important;
  font-family: inherit !important;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5 !important;
  }

    &--active {
    background-color: #ECF2DE !important;
  }
}

.greeting-card__tag-sales {
  color: #ffffff;
  font-size: 10px;
  vertical-align: middle;
  font-weight: 600;
  background: #cb6222;
  border-radius: 4px;
  white-space: nowrap;
  padding: 3px 8px 4px 8px;
  margin-left: 8px;
}

.greeting-card__compare-price {
  font-size: 14px;
  color: #7F7F7F;
  margin-left: 5px;
  text-decoration: line-through;
}

.label-v3 {
  font-weight: 400;
  font-size: 24px;
  line-height: 28px;
  letter-spacing: 0px;
  color: #000;
  margin-bottom: 8px;
  display: block;
  text-align: left;

  &--centered {
    text-align: center;
  }
}

.cards-selection-v3 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 9px;
  padding: 4px;

  &--history {
    grid-template-columns: repeat(auto-fit, 64px) !important;
    justify-content: center !important;
  }
}

.card-option-v3 {
  flex: 0 0 64px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  width: fit-content; /* Đảm bảo width bao khít thẻ img màu vàng/kem */

  &__img {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    overflow: hidden;
    background: #eae2d7;
    border: 8px solid transparent;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 4px;
    }
  }

  &--active {
    .card-option-v3__img {
    }
  }

  &--expanded {
    grid-column: span 3;
    grid-row: span 3;

    .card-option-v3__img {
      width: 216px !important;
      height: 216px !important;
      border-radius: 20px !important;
    }
  }

  &__check {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #5b841b;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
    z-index: 2;
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }

  /* Transition for checkmark */
  .check-fade-enter-active {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .check-fade-leave-active {
    transition: all 0.2s ease;
  }
  .check-fade-enter-from,
  .check-fade-leave-to {
    transform: scale(0.5);
    opacity: 0;
  }

  &--no {
    .card-option-v3__img {
       background: #fff;
       border: 1px solid #000;
       font-weight: 700;
       font-size: 16px;
    }
    &.card-option-v3--active {
      .card-option-v3__img {
         background: #ECF2DE;
      }
    }
  }
}

.price-value-v3 {
  font-weight: 700;
  font-size: 26px;
  line-height: 37.5px;
  min-height: 37.5px; /* Giữ khoảng trống bằng đúng line-height */
  color: #5D8C16;
  margin-top: 8px;
}

.upsell-divider-v3 {
  height: 1px;
  background: #E9E9E9;
  margin: 8px 0 16px;
}

.upsell-table-v3 {
  &__title {
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
    letter-spacing: 0px;
    margin: 0 0 12px;
    color: #000;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 0.5px solid #B3B3B3;
    font-size: 24px;
    line-height: 24px;
    letter-spacing: 0.5px;
    color: #7F7F7F;
  }
}

.mb-md {
  margin-bottom: 16px;
}
</style>
